import { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from 'react-bootstrap-v5';
import Swal from "sweetalert2";
import { uniqueId } from "lodash";
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';
import cartaoAnexoService, {
    ANEXO_CONTENT_TYPES,
    ANEXO_EXTENSOES,
    ANEXO_FORMATOS_DESCRICAO,
    ANEXO_QUANTIDADE_MAXIMA,
    ANEXO_TAMANHO_MAXIMO_BYTES,
    ICartaoAnexo,
} from "../../services/cartaoAnexoService";

// DEVN3-604/689 — anexos (imagens/PDF) dos cartões de Imobilizado, Salário e Provisão de Folha:
// selecionar, arrastar ou colar (Ctrl+V), visualizar dentro do cartão e remover. Os anexos entram na
// seção Anexos da capa. Quem decide se o cartão aceita anexo é o backend (a capa precisa ter a seção).

type Envio = { key: string; nome: string; tamanho: number; status: 'enviando' | 'erro'; mensagem?: string };

const formatarTamanho = (bytes: number) =>
    bytes >= 1024 * 1024
        ? `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`
        : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const isPdf = (contentType: string) => contentType === 'application/pdf';

function extensaoDe(nome: string) {
    const partes = nome.toLowerCase().split('.');
    return partes.length > 1 ? partes[partes.length - 1] : '';
}

function AreaAnexos({ cartaoId }: { cartaoId: number }) {
    const { t } = useTranslation();
    const [anexos, setAnexos] = useState<ICartaoAnexo[]>([]);
    const [envios, setEnvios] = useState<Envio[]>([]);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [arrastando, setArrastando] = useState<boolean>(false);
    const [visualizando, setVisualizando] = useState<ICartaoAnexo | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const anexosRef = useRef<ICartaoAnexo[]>([]);
    const enviandoRef = useRef<number>(0);

    anexosRef.current = anexos;

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            setAnexos(await cartaoAnexoService.listar(cartaoId));
        } catch {
            setAnexos([]);
        } finally {
            setCarregando(false);
        }
    }, [cartaoId]);

    useEffect(() => {
        setEnvios([]);
        setVisualizando(null);
        carregar();
    }, [carregar]);

    const validar = useCallback((arquivo: File): string | null => {
        const tipoOk = ANEXO_CONTENT_TYPES.includes(arquivo.type) || (!arquivo.type && ANEXO_EXTENSOES.includes(extensaoDe(arquivo.name)));
        if (!tipoOk)
            return t(`Este tipo de arquivo não é aceito. Envie um arquivo em um dos formatos permitidos: ${ANEXO_FORMATOS_DESCRICAO}.`);
        if (arquivo.size > ANEXO_TAMANHO_MAXIMO_BYTES)
            return t(`O arquivo ultrapassa o limite de ${ANEXO_TAMANHO_MAXIMO_BYTES / (1024 * 1024)} MB.`);
        if (arquivo.size === 0)
            return t("Não foi possível anexar o arquivo. Tente novamente.");
        if (anexosRef.current.length + enviandoRef.current >= ANEXO_QUANTIDADE_MAXIMA)
            return t(`Este cartão já possui o máximo de ${ANEXO_QUANTIDADE_MAXIMA} anexos. Exclua um anexo para enviar outro.`);
        return null;
    }, [t]);

    const enviar = useCallback(async (arquivos: File[]) => {
        for (const arquivo of arquivos) {
            const key = uniqueId('anexo_');
            const erro = validar(arquivo);
            if (erro) {
                setEnvios(old => [...old, { key, nome: arquivo.name, tamanho: arquivo.size, status: 'erro', mensagem: erro }]);
                continue;
            }

            enviandoRef.current++;
            setEnvios(old => [...old, { key, nome: arquivo.name, tamanho: arquivo.size, status: 'enviando' }]);
            try {
                const anexo = await cartaoAnexoService.anexar(cartaoId, arquivo);
                setAnexos(old => [...old, anexo]);
                setEnvios(old => old.filter(e => e.key !== key));
            } catch (error: any) {
                const mensagem = error?.response?.data?.message ?? t("Não foi possível anexar o arquivo. Tente novamente.");
                setEnvios(old => old.map(e => e.key === key ? { ...e, status: 'erro', mensagem } : e));
            } finally {
                enviandoRef.current--;
            }
        }
    }, [cartaoId, validar, t]);

    // Ctrl+V de imagem em qualquer ponto do cartão (menos dentro de campos de texto, para não
    // atrapalhar colar texto na Descrição).
    useEffect(() => {
        const onPaste = (e: ClipboardEvent) => {
            const alvo = e.target as HTMLElement | null;
            if (alvo && (alvo.tagName === 'TEXTAREA' || alvo.tagName === 'INPUT' || alvo.isContentEditable)) return;

            const arquivos = Array.from(e.clipboardData?.files ?? []);
            if (arquivos.length === 0) return;

            e.preventDefault();
            const agora = new Date();
            const carimbo = `${agora.getFullYear()}${String(agora.getMonth() + 1).padStart(2, '0')}${String(agora.getDate()).padStart(2, '0')}-${String(agora.getHours()).padStart(2, '0')}${String(agora.getMinutes()).padStart(2, '0')}${String(agora.getSeconds()).padStart(2, '0')}`;
            // Print colado vem como "image.png" — renomeia para não ficar tudo com o mesmo nome.
            const renomeados = arquivos.map((f, i) =>
                f.name === 'image.png' || !f.name
                    ? new File([f], `colado-${carimbo}${arquivos.length > 1 ? `-${i + 1}` : ''}.${extensaoDe(f.name) || 'png'}`, { type: f.type })
                    : f);
            enviar(renomeados);
        };

        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
    }, [enviar]);

    const remover = useCallback(async (anexo: ICartaoAnexo) => {
        const confirmacao = await Swal.fire({
            text: t(`Remover o anexo "${anexo.nomeOriginal}"?`),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t("Remover"),
            cancelButtonText: t("Cancelar"),
        });
        if (!confirmacao.isConfirmed) return;

        try {
            await cartaoAnexoService.excluir(cartaoId, anexo.id);
            setAnexos(old => old.filter(a => a.id !== anexo.id));
            setVisualizando(v => v?.id === anexo.id ? null : v);
        } catch (error: any) {
            Swal.fire({
                text: error?.response?.data?.message ?? t("Não foi possível remover o anexo. Tente novamente."),
                icon: 'error',
                timer: 4000,
                showConfirmButton: false,
            });
        }
    }, [cartaoId, t]);

    return (
        <div className="form-group mt-4">
            <label className="h5">{t("Anexos")}</label>

            <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
                onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
                onDragLeave={() => setArrastando(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setArrastando(false);
                    enviar(Array.from(e.dataTransfer.files));
                }}
                style={{
                    border: `2px dashed ${arrastando ? '#3f72ab' : '#ccc'}`,
                    borderRadius: '6px',
                    padding: '14px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: arrastando ? '#f0f5fb' : 'transparent',
                    color: '#666',
                }}
            >
                <i className="fa fa-paperclip me-2" />
                <strong>{t("Anexar documento")}</strong>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    {t("Clique, arraste o arquivo aqui ou cole uma imagem com Ctrl+V.")}
                    <br />
                    {t(`${ANEXO_FORMATOS_DESCRICAO} — até ${ANEXO_TAMANHO_MAXIMO_BYTES / (1024 * 1024)} MB por arquivo, máximo de ${ANEXO_QUANTIDADE_MAXIMA} anexos.`)}
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={ANEXO_CONTENT_TYPES.join(',')}
                    style={{ display: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        const arquivos = Array.from(e.target.files ?? []);
                        e.target.value = '';
                        enviar(arquivos);
                    }}
                />
            </div>

            {envios.length > 0 && (
                <ul className="list-unstyled mt-3 mb-0">
                    {envios.map(envio => (
                        <li key={envio.key} className="d-flex align-items-center mb-1" style={{ fontSize: '13px' }}>
                            {envio.status === 'enviando'
                                ? <span className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                : <i className="fa fa-times-circle text-danger me-2" />}
                            <span className="me-2">{envio.nome} ({formatarTamanho(envio.tamanho)})</span>
                            {envio.status === 'enviando'
                                ? <span className="text-muted">{t("Enviando...")}</span>
                                : <span className="text-danger">{envio.mensagem}</span>}
                            {envio.status === 'erro' && (
                                <i
                                    className="fa fa-times ms-auto"
                                    role="button"
                                    title={t("Dispensar")}
                                    style={{ cursor: 'pointer', color: 'gray' }}
                                    onClick={() => setEnvios(old => old.filter(e => e.key !== envio.key))}
                                />
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {carregando ? (
                <div className="mt-3 text-muted" style={{ fontSize: '13px' }}>{t("Carregando anexos...")}</div>
            ) : anexos.length > 0 && (
                <div className="d-flex flex-wrap mt-3" style={{ gap: '10px' }}>
                    {anexos.map(anexo => (
                        <div
                            key={anexo.id}
                            style={{ width: '120px', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden', position: 'relative', backgroundColor: '#fff' }}
                        >
                            <div
                                role="button"
                                title={t("Visualizar")}
                                onClick={() => setVisualizando(anexo)}
                                style={{ height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f7f7f7' }}
                            >
                                {isPdf(anexo.contentType) || !anexo.url
                                    ? <i className={`fa ${isPdf(anexo.contentType) ? 'fa-file-pdf' : 'fa-image'}`} style={{ fontSize: '36px', color: isPdf(anexo.contentType) ? '#C0392B' : 'gray' }} />
                                    : <img src={anexo.url} alt={anexo.nomeOriginal} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                            </div>
                            <div style={{ padding: '4px 6px', fontSize: '11px' }}>
                                <div title={anexo.nomeOriginal} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{anexo.nomeOriginal}</div>
                                <div className="d-flex align-items-center" style={{ color: 'gray' }}>
                                    <span>{formatarTamanho(anexo.tamanhoBytes)}</span>
                                    <i
                                        className="fa fa-trash ms-auto"
                                        role="button"
                                        title={t("Remover")}
                                        style={{ cursor: 'pointer', color: '#C0392B' }}
                                        onClick={() => remover(anexo)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal show={!!visualizando} onHide={() => setVisualizando(null)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '16px', wordBreak: 'break-all' }}>{visualizando?.nomeOriginal}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: 0, backgroundColor: '#525659', textAlign: 'center' }}>
                    {visualizando && (!visualizando.url ? (
                        <div className="p-5 text-white">{t("Não foi possível carregar o arquivo.")}</div>
                    ) : isPdf(visualizando.contentType) ? (
                        <iframe title={visualizando.nomeOriginal} src={visualizando.url} style={{ width: '100%', height: '80vh', border: 0 }} />
                    ) : (
                        <img src={visualizando.url} alt={visualizando.nomeOriginal} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    {visualizando?.url && (
                        <a className="btn btn-light btn-sm" href={visualizando.url} target="_blank" rel="noopener noreferrer">
                            <i className="fa fa-external-link-alt me-1" />{t("Abrir em nova aba")}
                        </a>
                    )}
                    {visualizando && (
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => remover(visualizando)}>
                            <i className="fa fa-trash me-1" />{t("Remover")}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
}

function AnexosCartao({ cartaoId }: { cartaoId: number }) {
    const [permitido, setPermitido] = useState<boolean>(false);

    useEffect(() => {
        let ativo = true;
        setPermitido(false);
        cartaoAnexoService.disponibilidade(cartaoId)
            .then(ok => { if (ativo) setPermitido(ok); })
            .catch(() => { if (ativo) setPermitido(false); });
        return () => { ativo = false; };
    }, [cartaoId]);

    return permitido ? <AreaAnexos cartaoId={cartaoId} /> : null;
}

export default AnexosCartao;
